(() => {
  const SECTION_SELECTOR = "main[data-template*='product'] .product-recommendations";
  const SLIDER_SELECTOR = 'slideshow-component[autoplay]';
  const INITIALIZED_ATTR = 'data-inknfiber-related-slider';
  const RESUME_DELAY = 1200;
  const EDGE_TOLERANCE = 3;

  function suspend(slider) {
    if (typeof slider.suspend === 'function') {
      slider.suspend();
    }
  }

  function resume(slider) {
    if (typeof slider.resume === 'function') {
      slider.resume();
    }
  }

  function getScroller(slider) {
    return slider.querySelector('slideshow-slides[ref="scroller"], slideshow-slides');
  }

  function getSlides(slider) {
    const scroller = getScroller(slider);
    if (!scroller) return [];

    return Array.from(scroller.querySelectorAll('.resource-list__slide')).filter((slide) => !slide.hasAttribute('hidden'));
  }

  function getSlidePositions(slider) {
    const slides = getSlides(slider);

    return slides.map((slide) => slide.offsetLeft);
  }

  function closestSlideIndex(positions, scrollLeft) {
    if (!positions.length) return 0;

    let closestIndex = 0;
    let closestDistance = Math.abs(positions[0] - scrollLeft);

    positions.forEach((position, index) => {
      const distance = Math.abs(position - scrollLeft);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  function oneCardStep(slider) {
    const positions = getSlidePositions(slider);

    if (positions.length > 1) {
      return Math.abs(positions[1] - positions[0]);
    }

    const firstSlide = getSlides(slider)[0];
    const scroller = getScroller(slider);
    if (!firstSlide || !scroller) return 0;

    const styles = window.getComputedStyle(scroller);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;

    return firstSlide.getBoundingClientRect().width + gap;
  }

  function moveByOneCard(slider, direction, event) {
    event?.preventDefault();

    const scroller = getScroller(slider);
    const positions = getSlidePositions(slider);
    if (!scroller || positions.length <= 1) return;

    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const currentLeft = scroller.scrollLeft;
    const step = oneCardStep(slider);
    const currentIndex = closestSlideIndex(positions, currentLeft);
    let targetIndex = currentIndex + direction;

    if (direction > 0 && currentLeft >= maxScroll - EDGE_TOLERANCE) {
      targetIndex = 0;
    } else if (direction < 0 && currentLeft <= EDGE_TOLERANCE) {
      targetIndex = positions.length - 1;
    }

    if (direction > 0 && targetIndex >= positions.length) {
      targetIndex = 0;
    } else if (direction < 0 && targetIndex < 0) {
      targetIndex = positions.length - 1;
    }

    let targetLeft = positions[targetIndex] ?? currentLeft + step * direction;
    targetLeft = Math.max(0, Math.min(targetLeft, maxScroll));

    if (direction > 0 && Math.abs(targetLeft - currentLeft) < EDGE_TOLERANCE && currentLeft < maxScroll - EDGE_TOLERANCE) {
      targetLeft = Math.min(currentLeft + step, maxScroll);
    }

    if (direction < 0 && Math.abs(targetLeft - currentLeft) < EDGE_TOLERANCE && currentLeft > EDGE_TOLERANCE) {
      targetLeft = Math.max(currentLeft - step, 0);
    }

    scroller.scrollTo({
      left: targetLeft,
      behavior: 'smooth',
    });

    if ('current' in slider) {
      slider.current = targetIndex;
    }
  }

  function applyOneCardControls(slider) {
    const isRtl = window.getComputedStyle(slider).direction === 'rtl' || document.documentElement.dir === 'rtl';
    const forwardDirection = isRtl ? -1 : 1;

    slider.next = (event) => moveByOneCard(slider, forwardDirection, event);
    slider.previous = (event) => moveByOneCard(slider, -forwardDirection, event);
  }

  function bindArrowButtons(slider) {
    const previousButton = slider.querySelector('.slideshow-control--previous');
    const nextButton = slider.querySelector('.slideshow-control--next');

    previousButton?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        slider.previous(event);
      },
      true
    );

    nextButton?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        slider.next(event);
      },
      true
    );
  }

  function applyMotionPreference(slider, motionQuery) {
    if (motionQuery.matches) {
      if (typeof slider.pause === 'function') {
        slider.pause();
      } else {
        suspend(slider);
      }
    } else if (slider.hasAttribute('paused')) {
      slider.removeAttribute('paused');
      resume(slider);
    }
  }

  function initSlider(slider) {
    if (!slider || slider.getAttribute(INITIALIZED_ATTR) === 'true') return;

    slider.setAttribute(INITIALIZED_ATTR, 'true');
    applyOneCardControls(slider);
    bindArrowButtons(slider);

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    applyMotionPreference(slider, motionQuery);

    let resumeTimeout = 0;

    function resumeSoon() {
      window.clearTimeout(resumeTimeout);
      if (motionQuery.matches) return;
      resumeTimeout = window.setTimeout(() => resume(slider), RESUME_DELAY);
    }

    slider.addEventListener('pointerenter', () => suspend(slider));
    slider.addEventListener('pointerleave', resumeSoon);
    slider.addEventListener('focusin', () => suspend(slider));
    slider.addEventListener('focusout', resumeSoon);
    slider.addEventListener('touchstart', () => suspend(slider), { passive: true });
    slider.addEventListener('touchend', resumeSoon, { passive: true });
    slider.addEventListener('touchcancel', resumeSoon, { passive: true });

    const handleMotionChange = () => applyMotionPreference(slider, motionQuery);

    if (typeof motionQuery.addEventListener === 'function') {
      motionQuery.addEventListener('change', handleMotionChange);
    } else if (typeof motionQuery.addListener === 'function') {
      motionQuery.addListener(handleMotionChange);
    }
  }

  function init(scope = document) {
    scope.querySelectorAll(`${SECTION_SELECTOR} ${SLIDER_SELECTOR}`).forEach(initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) {
          init(node);
        }
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
